import _ from 'lodash';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { DEFAULT_TOOL_ITEM_PROPS } from '@/tools/tool.constants';
import { getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import type { FftState } from '@/tools/frequencyAnalysis/fft.store';
import type { ToolConfigParams, ToolConfigValue, ToolItem } from '@/tools/tool.types';

/**
 * Builds the UI config object that is persisted with the FFT calculated item. Base tool fields and the
 * `signalToAggregate` parameter slot are stored separately and therefore omitted.
 */
export function getFftConfigParams(state: FftState): ToolConfigParams {
  return getToolConfigParams({
    serializedState: state as unknown as Record<string, ToolConfigValue>,
    toolType: TREND_TOOLS.FFT_TABLE,
    parameterDefinitionKeys: ['signalToAggregate'],
    advancedParametersCollapsed: state.advancedParametersCollapsed,
  });
}

/**
 * Computes the next `signalToAggregate` value for the FFT store. Accepts an item, an object with an id, or a plain
 * id string, sanitizes it down to the persisted tool-item properties, and returns the current value unchanged when
 * the sanitized item is deeply equal (so the store can avoid a redundant update).
 */
export function nextSignalToAggregate(
  current: ToolItem | null | undefined,
  item: ToolItem | { id: string } | string | null | undefined,
): ToolItem | null | undefined {
  const normalized = typeof item === 'string' ? { id: item } : item;
  const sanitized = (sanitizeToolItem(normalized, DEFAULT_TOOL_ITEM_PROPS) ?? undefined) as ToolItem | undefined;

  return _.isEqual(current, sanitized) ? current : sanitized;
}
